import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useNavPrefetcher } from '@/components/layout/use-nav-prefetcher';
import { ModelHealthTile, MoreSheet } from '@/components/layout/MoreSheet';
import {
  isModelOutputNavVisible,
  useModelOutputAccess,
} from '@/lib/compliance/model-output-access';
import { useMarket } from '@/lib/market/market-context';
import {
  glassTabBar,
  publishTabbarHeight,
  type GlassTabItem,
} from '@/lib/native/glass-tab-bar';

/**
 * =============================================================================
 *  lg 以下的底部分頁列（手機與直拿的平板；桌面的 SideNav 在這個寬度不出現）。
 *
 *  五格是每天都會打開的那五件事：儀表板／自選股／庫存股／AI 助理／更多。
 *  其餘頁面與整份 registry 指標清單收進「更多」抽屜——清單本身是 SideNav 的
 *  `MetricNavGroups`，同一份元件，所以手機上的指標選單永遠跟桌面一致，
 *  這裡也不知道任何 metric 的名字（SideNav 開頭的驗收 #2 同樣適用）。
 *
 *  高度 = --tabbar-h（56px）+ --safe-bottom（Home 指示條）。__root.tsx 用
 *  `pb-tabbar` 讓出同一段高度，最後一列內容與頁尾才不會被蓋住。
 * ========================================================================== */

const PRIMARY_TAB = /^\/(tw|us)\/(dashboard|watchlist|portfolio|assistant)(\/|$)/;
const AUTH_PAGE = /^\/(login|register|forgot-password|reset-password)(\/|$)/;

/** TanStack 的 activeProps.className 是**接在** className 後面，不是取代；顏色放在 render prop 裡決定，
 *  才不會同時出現兩個 text-* 而由 CSS 產出順序決定誰贏。 */
const TAB = 'flex h-full w-full min-w-0 flex-col items-center justify-center gap-0.5 px-1 text-11 leading-none';

function TabLabel({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <span className={`max-w-full truncate ${active ? 'font-semibold text-fg-1' : 'text-muted'}`}>
      {children}
    </span>
  );
}

function TabIcon({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <span
      className={[
        'flex h-7 w-12 items-center justify-center rounded-pill',
        active ? 'bg-accent/15 text-accent' : 'text-fg-2',
      ].join(' ')}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 24 24"
        className="size-[22px]"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {children}
      </svg>
    </span>
  );
}

const ICONS = {
  dashboard: (
    <>
      <rect x="3.5" y="3.5" width="7" height="8" rx="2" />
      <rect x="13.5" y="3.5" width="7" height="5" rx="2" />
      <rect x="13.5" y="11.5" width="7" height="9" rx="2" />
      <rect x="3.5" y="14.5" width="7" height="6" rx="2" />
    </>
  ),
  watchlist: (
    <path d="M12 3.8l2.5 5.1 5.6.8-4 3.9.9 5.6-5-2.6-5 2.6.9-5.6-4-3.9 5.6-.8z" />
  ),
  portfolio: (
    <>
      <rect x="3.5" y="7.5" width="17" height="12" rx="2.5" />
      <path d="M9 7.5V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1.5M3.5 13h17" />
    </>
  ),
  assistant: (
    <>
      <path d="M5 5.5h14a1.5 1.5 0 0 1 1.5 1.5v8.5A1.5 1.5 0 0 1 19 17h-7l-4.5 3.5V17H5a1.5 1.5 0 0 1-1.5-1.5V7A1.5 1.5 0 0 1 5 5.5z" />
      <path d="M8.5 11.3h.01M12 11.3h.01M15.5 11.3h.01" strokeWidth="2.6" />
    </>
  ),
  more: (
    <>
      <circle cx="5.5" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
} as const;

function TabLink({
  to,
  label,
  icon,
}: {
  to: '/$market/dashboard' | '/$market/watchlist' | '/$market/portfolio' | '/$market/assistant';
  label: string;
  icon: ReactNode;
}) {
  const { market } = useMarket();
  return (
    <Link
      to={to}
      params={{ market }}
      activeOptions={{ exact: false }}
      className={TAB}
    >
      {({ isActive }) => (
        <>
          <TabIcon active={isActive}>{icon}</TabIcon>
          <TabLabel active={isActive}>{label}</TabLabel>
        </>
      )}
    </Link>
  );
}

const NATIVE_TABS: readonly GlassTabItem[] = [
  { id: 'dashboard', title: '今日重點', symbol: 'square.grid.2x2' },
  { id: 'watchlist', title: '自選股', symbol: 'star' },
  { id: 'portfolio', title: '庫存股', symbol: 'briefcase' },
  { id: 'assistant', title: 'AI 助理', symbol: 'bubble.left.and.text.bubble.right' },
  { id: 'more', title: '更多', symbol: 'ellipsis' },
];

/** 目前路徑對應哪一個原生分頁；不在前四格的頁面都算「更多」。 */
function nativeTabFor(pathname: string): string {
  const hit = /^\/(?:tw|us)\/(dashboard|watchlist|portfolio|assistant)(?:\/|$)/.exec(pathname);
  return hit?.[1] ?? 'more';
}

export function BottomTabBar() {
  const { market } = useMarket();
  const [moreOpen, setMoreOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const access = useModelOutputAccess();
  // 預取器住在這裡而不是抽屜裡：點下指標的同一刻抽屜就卸載了。
  const prefetcher = useNavPrefetcher();

  const close = () => {
    setMoreOpen(false);
  };

  // ---- iOS App：用系統原生分頁列（Liquid Glass），HTML 分頁列不畫 -----------------
  const native = glassTabBar();
  const navigate = useNavigate();
  const onAuthPage = AUTH_PAGE.test(pathname);
  const selectedId = moreOpen ? 'more' : nativeTabFor(pathname);
  const latest = useRef({ market, navigate, selectedId });
  useEffect(() => {
    latest.current = { market, navigate, selectedId };
  });

  // 顯示／隱藏：登入頁與打開「更多」抽屜時收起（抽屜是全螢幕，原生列會蓋住它的底部）。
  const showNative = native !== null && !onAuthPage && !moreOpen;
  useEffect(() => {
    if (native === null) return;
    if (!showNative) {
      void native.hide().then(() => {
        publishTabbarHeight(0);
      });
      return;
    }
    let cancelled = false;
    void native
      .configure({ items: [...NATIVE_TABS], selectedId: latest.current.selectedId })
      .then((result) => {
        if (!cancelled) publishTabbarHeight(result.height);
      });
    return () => {
      cancelled = true;
    };
  }, [native, showNative]);

  useEffect(() => {
    if (native === null || !showNative) return;
    void native.setSelected({ id: selectedId });
  }, [native, showNative, selectedId]);

  useEffect(() => {
    if (native === null) return;
    const handle = native.addListener('tabSelected', ({ id }) => {
      const { market: m, navigate: go } = latest.current;
      if (id === 'more') {
        setMoreOpen(true);
        return;
      }
      if (id === 'dashboard' || id === 'watchlist' || id === 'portfolio' || id === 'assistant') {
        void go({ to: `/$market/${id}`, params: { market: m } });
      }
    });
    const heightHandle = native.addListener('heightChanged', ({ height }) => {
      publishTabbarHeight(height);
    });
    return () => {
      void Promise.resolve(handle).then((h) => h.remove());
      void Promise.resolve(heightHandle).then((h) => h.remove());
    };
  }, [native]);
  // 目前這一頁不是前四格之一 → 「更多」亮起，使用者才知道自己在哪一格底下。
  const moreActive = moreOpen || (!PRIMARY_TAB.test(pathname) && !AUTH_PAGE.test(pathname));

  const modelTile = isModelOutputNavVisible(access) ? <ModelHealthTile onNavigate={close} /> : null;

  if (native !== null) {
    // 原生分頁列由系統畫；這裡只負責「更多」面板（由原生分頁列的「更多」開啟）。
    return <MoreSheet open={moreOpen} onOpenChange={setMoreOpen} prefetcher={prefetcher} extra={modelTile} />;
  }

  return (
    <nav
      aria-label="主要分頁"
      data-testid="bottom-tab-bar"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-border-1/60 bg-surface-1/95 pr-[var(--safe-right)] pb-[var(--safe-bottom)] pl-[var(--safe-left)] backdrop-blur lg:hidden"
    >
      <ul className="grid h-[var(--tabbar-h)] grid-cols-5">
        <li>
          <TabLink to="/$market/dashboard" label="今日重點" icon={ICONS.dashboard} />
        </li>
        <li>
          <TabLink to="/$market/watchlist" label="自選股" icon={ICONS.watchlist} />
        </li>
        <li>
          <TabLink to="/$market/portfolio" label="庫存股" icon={ICONS.portfolio} />
        </li>
        <li>
          <TabLink to="/$market/assistant" label="AI 助理" icon={ICONS.assistant} />
        </li>
        <li>
          <MoreSheet
            open={moreOpen}
            onOpenChange={setMoreOpen}
            prefetcher={prefetcher}
            extra={modelTile}
            trigger={
              <button type="button" className={TAB} data-testid="bottom-tab-more">
                <TabIcon active={moreActive}>{ICONS.more}</TabIcon>
                <TabLabel active={moreActive}>更多</TabLabel>
              </button>
            }
          />
        </li>
      </ul>
    </nav>
  );
}
